// Texterna i inkorgen: lägena, datumen, kundens svar och händelserna (ADR 0069).
import type { components } from '../api/schema.js';
import { BOLAGSFORMER } from '../katalog/texter.js';

export type Post = components['schemas']['InboxItem'];
export type Lage = components['schemas']['InboxState'];
export type Detalj = components['schemas']['InquiryDetail'];
export type Filter = 'all' | 'approve' | 'review' | 'questions' | 'sent' | 'accepted';

export const FILTER: { kod: Filter; namn: string }[] = [
  { kod: 'all', namn: 'Alla' },
  { kod: 'approve', namn: 'Att godkänna' },
  { kod: 'review', namn: 'Granskas' },
  { kod: 'questions', namn: 'Frågor' },
  { kod: 'sent', namn: 'Skickade' },
  { kod: 'accepted', namn: 'Accepterade' },
];

export const arFilter = (v: string | null): v is Filter => FILTER.some((f) => f.kod === v);

const LAGE: Record<Lage, { namn: string; chip: string }> = {
  approve: { namn: 'Väntar på godkännande', chip: 'chip-gul' },
  review: { namn: 'Granskas', chip: 'chip-rod' },
  question: { namn: 'Fråga', chip: 'chip-gra' },
  message: { namn: 'Besvaras av er', chip: 'chip-gra' },
  scheduled: { namn: 'Skickas snart', chip: 'chip-gra' },
  sent: { namn: 'Skickad', chip: 'chip-gron' },
  viewed: { namn: 'Skickad · öppnad', chip: 'chip-gron' },
  accepted: { namn: 'Accepterad', chip: 'chip-fylld' },
  declined: { namn: 'Avböjd av kunden', chip: 'chip-gra' },
  expired: { namn: 'Utgången', chip: 'chip-gra' },
  cancelled: { namn: 'Stoppad', chip: 'chip-gra' },
};

type Orsak = { code: string; [k: string]: unknown };

/** Läget i klartext. En offert som granskas visar den första flaggan, till exempel "Granskas · företagsköp". */
export function lageText(lage: Lage, orsaker: Orsak[] = []): string {
  if (lage !== 'review') return LAGE[lage].namn;
  const flaggad = orsaker.find((o) => o.code === 'flagged');
  const flagga = Array.isArray(flaggad?.flags)
    ? (flaggad.flags as { code: string }[])[0]
    : undefined;
  return flagga ? `Granskas · ${flagga.code}` : LAGE.review.namn;
}

export const lageChip = (lage: Lage) => LAGE[lage].chip;

/** Väntar offerten på byrån? Då kan den godkännas, ändras eller avslås. */
export const vantar = (lage: Lage) => lage === 'approve' || lage === 'review';

const STOCKHOLM = 'Europe/Stockholm';
const dag = (d: Date) => d.toLocaleDateString('sv-SE', { timeZone: STOCKHOLM });

/** "10:42" i dag, "I går", annars "30 sep" eller "30 sep 2025" ett annat år. */
export function mottagenText(iso: string, nu = new Date()): string {
  const d = new Date(iso);
  if (dag(d) === dag(nu)) return klockslag(iso);
  const igar = new Date(nu.getTime() - 24 * 60 * 60 * 1000);
  if (dag(d) === dag(igar)) return 'I går';
  const sammaAr =
    d.toLocaleDateString('sv-SE', { timeZone: STOCKHOLM, year: 'numeric' }) ===
    nu.toLocaleDateString('sv-SE', { timeZone: STOCKHOLM, year: 'numeric' });
  return d
    .toLocaleDateString('sv-SE', {
      timeZone: STOCKHOLM,
      day: 'numeric',
      month: 'short',
      ...(sammaAr ? {} : { year: 'numeric' }),
    })
    .replace('.', '');
}

export const klockslag = (iso: string) =>
  new Date(iso).toLocaleTimeString('sv-SE', {
    timeZone: STOCKHOLM,
    hour: '2-digit',
    minute: '2-digit',
  });

/** "5 okt 10:42", till händelserna. */
export const tidpunkt = (iso: string) =>
  `${new Date(iso).toLocaleDateString('sv-SE', { timeZone: STOCKHOLM, day: 'numeric', month: 'short' }).replace('.', '')} ${klockslag(iso)}`;

/** Kundens namn i listan: företaget om det finns, annars personen. */
export function kundnamn(kund: Post['customer']): { namn: string; under: string | null } {
  if (!kund) return { namn: 'Kunduppgifterna är gallrade', under: null };
  if (kund.companyName) return { namn: kund.companyName, under: kund.name };
  return { namn: kund.name, under: null };
}

export const bolagsformText = (kod: unknown) =>
  BOLAGSFORMER.find((b) => b.kod === kod)?.namn ??
  (kod === 'annan' ? 'Annat, eller inte startat än' : typeof kod === 'string' ? kod : null);

const HANTERING: Record<string, string> = {
  digital: 'Digitalt system',
  paper: 'Papper',
  unknown: 'Vet inte',
};

const SVAR: { nyckel: string; namn: string; text?: (v: unknown) => string | null }[] = [
  { nyckel: 'legalForm', namn: 'Bolagsform', text: bolagsformText },
  { nyckel: 'business', namn: 'Verksamhet' },
  { nyckel: 'invoicesOut', namn: 'Kundfakturor per månad' },
  { nyckel: 'invoicesIn', namn: 'Leverantörsfakturor per månad' },
  {
    nyckel: 'invoiceHandling',
    namn: 'Hantering',
    text: (v) => (typeof v === 'string' ? (HANTERING[v] ?? v) : null),
  },
  {
    nyckel: 'turnover',
    namn: 'Omsättning per år',
    text: (v) => (v === 'vet ej' ? 'Vet inte' : typeof v === 'string' ? v : null),
  },
  { nyckel: 'employees', namn: 'Anställda med lön' },
  { nyckel: 'owners', namn: 'Ägare' },
];

function svarText(v: unknown): string | null {
  if (v === null || v === undefined || v === '') return null;
  if (v === 'vet ej') return 'Vet inte';
  if (typeof v === 'boolean') return v ? 'Ja' : 'Nej';
  if (typeof v === 'number') return v.toLocaleString('sv-SE');
  if (typeof v === 'string') return v;
  return JSON.stringify(v);
}

/** Formulärets svar i designens ordning, och andra svar sist med sitt fältnamn. */
export function svarLista(svar: Record<string, unknown>): { namn: string; varde: string }[] {
  const kanda = SVAR.flatMap(({ nyckel, namn, text }) => {
    const varde = (text ?? svarText)(svar[nyckel]);
    return varde === null ? [] : [{ namn, varde }];
  });
  const ovriga = Object.entries(svar).flatMap(([nyckel, v]) => {
    if (SVAR.some((s) => s.nyckel === nyckel)) return [];
    const varde = svarText(v);
    return varde === null ? [] : [{ namn: nyckel, varde }];
  });
  return [...kanda, ...ovriga];
}

/** Händelsen i klartext. */
export function handelseText(h: Detalj['events'][number], latencyMs?: number): string {
  switch (h.type) {
    case 'created':
      if (latencyMs === undefined) return 'Offerten framräknad';
      if (latencyMs < 1000) return 'Offerten framräknad på under en sekund';
      return `Offerten framräknad på ${(latencyMs / 1000).toLocaleString('sv-SE', { maximumFractionDigits: 1 })} s`;
    case 'review_requested':
      return Array.isArray(h.data.reasons) &&
        (h.data.reasons as Orsak[]).some((o) => o.code === 'approvalRequired')
        ? 'Väntar på ert godkännande'
        : 'Skickad till granskning';
    case 'edited':
      return 'Ändrad av byrån';
    case 'approved':
      return 'Godkänd';
    case 'scheduled':
      return 'Schemalagd för utskick';
    case 'sent':
      return 'Skickad till kunden';
    case 'viewed':
      return 'Kunden öppnade offerten';
    case 'accepted':
      return 'Kunden accepterade offerten';
    case 'declined':
      return 'Kunden avböjde';
    case 'expired':
      return 'Offerten gick ut';
    case 'cancelled':
      return 'Stoppad';
    default:
      return h.type;
  }
}

/** "38 min", "2 tim 5 min" eller "3 dagar". */
export function svarstidText(minuter: number | null): string {
  if (minuter === null) return '–';
  if (minuter < 1) return 'Under en minut';
  if (minuter < 60) return `${Math.round(minuter)} min`;
  if (minuter < 48 * 60) {
    const tim = Math.floor(minuter / 60);
    const min = Math.round(minuter % 60);
    return min ? `${tim} tim ${min} min` : `${tim} tim`;
  }
  return `${Math.round(minuter / (24 * 60))} dagar`;
}

/** Månadens namn, till exempel "oktober". */
export const manadNamn = (manad: string) =>
  new Date(`${manad}-15T12:00:00Z`).toLocaleDateString('sv-SE', {
    month: 'long',
    timeZone: STOCKHOLM,
  });
